import { useEffect, useMemo, useRef, useState, RefObject } from 'react';
import moment from 'moment';
import { Timeline, TimelineOptions, DataItem, DataGroup, DataSet } from 'vis-timeline/standalone';
import 'vis-timeline/styles/vis-timeline-graph2d.min.css';
import { GrantDto } from '../../../../../dtos/GrantDto';
import { exportTimelinePng } from '../../shared/exportTimelinePng';
import { loadCrossModuleData, RawProgressData } from '../../shared/loadProgressData';
import {
    AbsorbedPhase,
    buildProgressData,
    clusterEventsByDay,
    statusColor,
    ProgressData,
    GroupingMode,
    DepthMode,
    EventCluster,
    TimelineEvent,
    UnscheduledPhase,
    UnscheduledStep,
} from '../../shared/progressData';
import { ProgressModule } from '../../shared/moduleCatalog';
import { useAppContext } from '../../../../shared/AppContext';
import { downloadFileById } from '../../../../shared/fileDownload';
import { eventTriangleHtml, tickKind } from '../../../../shared/timeline/eventTicks';
import { HoverTip, HoverTipDay, TooltipEvent, mergeTicksToDays } from '../../../../shared/timeline/eventTooltipTypes';
import { buildDeepLinkHash } from '../../../../shared/tasklist/deepLinkUtils';

// Preserved for future use: the per-type icon "bubble" (file/comment/note icons + count). Not used
// while the timeline renders the original inverted-triangle marker via eventTriangleHtml above.
const eventBubbleHtml = (fileCount: number, commentCount: number, noteCount: number, total: number): string => {
    const icons: string[] = [];
    if (fileCount) icons.push('<i class="dx-icon-attach"></i>');
    if (commentCount) icons.push('<i class="dx-icon-comment"></i>');
    if (noteCount) icons.push('<i class="dx-icon-edit"></i>');
    if (!icons.length) icons.push('<i class="dx-icon-comment"></i>');
    return `<span class="pt-tick-bubble">${icons.join('')}<span class="pt-tick-count">${total}</span></span>`;
};

const clusterContentHtml = (c: EventCluster): string =>
    eventTriangleHtml(c.events.length, tickKind(c.noteCount, c.fileCount + c.commentCount));

// vis stashes its ItemSet under a few different names depending on build; resolve it once.
const getItemSet = (tl: any) => tl?.itemSet || tl?._itemSet || tl?.body?.itemSet;

// Force-regenerate clusters and restack groups — mirrors what vis does in setGroups/setOptions. Used
// to recover from the clustering-vs-collapsed-nested-group bug where a redraw tries to draw a cluster
// item whose parent group was detached ("Cannot redraw item: no parent attached").
const regenerateClusters = (itemSet: any) => {
    if (!itemSet?.options?.cluster || !itemSet.clusterGenerator) return;
    itemSet.clusterGenerator.updateData();
    itemSet._clusterItems();
    itemSet.markDirty({ refreshItems: true, restackGroups: true });
};

// requestAnimationFrame-coalesced call, matching how vis throttles its own redraw.
const rafThrottle = (fn: () => void): (() => void) => {
    let scheduled = false;
    return () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => { scheduled = false; fn(); });
    };
};

// Context line shown at the top of a tooltip — how much it spells out depends on the depth, since
// the deeper grouping headings already carry the rest of the location.
const tipContextHeader = (e: TimelineEvent, depth: DepthMode): string => {
    if (depth === 'modules') return '';
    if (depth === 'phases') return e.phaseName;
    return `${e.phaseName}${e.stepName ? ` – ${e.stepName}` : ''}`;
};

export interface VisTimelineControllerProps {
    grantData: GrantDto;
    // Bumped by the parent when a note is added so the timeline reloads and shows the new event
    // immediately instead of waiting for a page reload.
    refreshToken?: number;
}

export interface VisTimelineViewProps {
    containerRef: RefObject<HTMLDivElement>;
    exportPng: (captureEl: HTMLElement | null) => Promise<void>;
    loading: boolean;
    hasData: boolean;
    data: ProgressData | null;
    modules: ProgressModule[];
    visibleModuleKeys: string[];
    setVisibleModuleKeys: (keys: string[]) => void;
    depthMode: DepthMode;
    setDepthMode: (mode: DepthMode) => void;
    groupingMode: GroupingMode;
    setGroupingMode: (mode: GroupingMode) => void;
    showEvents: boolean;
    setShowEvents: (v: boolean) => void;
    showProjected: boolean;
    setShowProjected: (v: boolean) => void;
    showLabels: boolean;
    setShowLabels: (v: boolean) => void;
    // Show/hide rows for tasks/subtasks that have no start/end dates (event-only "unscheduled" rows).
    showUnscheduled: boolean;
    setShowUnscheduled: (v: boolean) => void;
    // The undated tasks/subtasks the chart draws no row for, for the banner to name.
    undatedNotShown: { phases: UnscheduledPhase[]; steps: UnscheduledStep[] };
    absorbedNotShown: AbsorbedPhase[];
    // Current zoomed/panned window and full data range — drive the zoom slider and horizontal scrollbar.
    windowStart: Date;
    windowEnd: Date;
    dataRangeMin: Date;
    dataRangeMax: Date;
    setTimelineWindow: (start: Date, end: Date) => void;
    zoomPercent: number;
    setZoomPercent: (v: number) => void;
    // Interactive hover tooltip (clickable file downloads).
    hoverTip: HoverTip | null;
    onTipEnter: () => void;
    onTipLeave: () => void;
    downloadEventFile: (fileId: string, fileName: string) => Promise<void>;
    openEventLocation: (ev: TooltipEvent, includeSubtask: boolean) => void;
}

const PROJECTED_FILL = 'rgba(150, 150, 150, 0.18)';

// Cluster only the event ticks (box items) together — never the range bars or hidden sizers.
// vis collapses overlapping ticks into a single count bubble when zoomed out and explodes them
// back into individual ticks as you zoom in (the "heat map" behavior).
const CLUSTER_OPTION = {
    maxItems: 1,
    showStipes: false,
    fitOnDoubleClick: true,
    clusterCriteria: (a: any, b: any) => a?.type === 'box' && b?.type === 'box',
};

// Cap how far in users can zoom. vis's `zoomMin` is the minimum visible window in ms — a 7-day
// floor keeps the time axis at day-level granularity (day numbers stay visible, no hour/minute
// columns). The zoom slider's MIN_WINDOW_MS below uses the same value so the two stay in sync.
const MIN_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

// Single source of options. `full` builds an unbounded, non-scrolling timeline used for PNG export
// (so the whole vertical content lays out and html2canvas can capture every row).
const makeTimelineOptions = (data: ProgressData, full: boolean): TimelineOptions => ({
    showCurrentTime: true,
    stack: false,
    groupHeightMode: 'fixed',
    zoomable: true,
    zoomKey: 'ctrlKey',
    zoomMin: MIN_WINDOW_MS,
    moveable: true,
    orientation: 'top',
    min: data.rangeMin,
    max: data.rangeMax,
    start: data.rangeMin,
    end: data.rangeMax,
    margin: { item: 6, axis: 6 },
    tooltip: { followMouse: false, overflowMethod: 'cap' },
    xss: { disabled: true },
    cluster: CLUSTER_OPTION,
    maxHeight: full ? undefined : 400,
    verticalScroll: !full,
});

type RowType = 'module' | 'task' | 'subtask';

const TYPE_LABEL: Record<RowType, string> = {
    module: 'Module',
    task: 'Task',
    subtask: 'Subtask',
};

const rowDisplayLabel = (r: RangeRow): string =>
    r.type === 'module'
        ? `${r.name} (${r.moduleAbbrev})`
        : r.name;

interface RangeRow {
    key: string;
    moduleKey: string;
    phaseKey?: string;
    type: RowType;
    name: string;
    moduleAbbrev: string;
    actualStart: Date;
    actualEnd: Date;
    projectedStart: Date;
    projectedEnd: Date;
    status: string;
}

const collectRows = (data: ProgressData, depth: DepthMode): RangeRow[] => {
    if (depth === 'modules') {
        return data.moduleAggregates.map(m => ({
            key: m.key,
            moduleKey: m.moduleKey,
            type: 'module' as RowType,
            name: m.name,
            moduleAbbrev: m.abbrev,
            actualStart: m.actualStart,
            actualEnd: m.actualEnd,
            projectedStart: m.projectedStart,
            projectedEnd: m.projectedEnd,
            status: m.status,
        }));
    }
    if (depth === 'phases') {
        // A merged phase draws no bar - the work moved past it and the phase after it covers its
        // span. Its row stays, so the task is still listed by name.
        return data.phases.filter(p => !p.collapsed).map(p => ({
            key: p.key,
            moduleKey: p.moduleKey,
            type: 'task' as RowType,
            name: p.name,
            moduleAbbrev: p.moduleAbbrev,
            actualStart: p.actualStart,
            actualEnd: p.actualEnd,
            projectedStart: p.projectedStart,
            projectedEnd: p.projectedEnd,
            status: p.status,
        }));
    }
    // depth === 'steps': render bars on module aggregate, task, and subtask rows
    const rows: RangeRow[] = [];
    data.moduleAggregates.forEach(m => rows.push({
        key: m.key,
        moduleKey: m.moduleKey,
        type: 'module',
        name: m.name,
        moduleAbbrev: m.abbrev,
        actualStart: m.actualStart,
        actualEnd: m.actualEnd,
        projectedStart: m.projectedStart,
        projectedEnd: m.projectedEnd,
        status: m.status,
    }));
    data.phases.filter(p => !p.collapsed).forEach(p => rows.push({
        key: p.key,
        moduleKey: p.moduleKey,
        type: 'task',
        name: p.name,
        moduleAbbrev: p.moduleAbbrev,
        actualStart: p.actualStart,
        actualEnd: p.actualEnd,
        projectedStart: p.projectedStart,
        projectedEnd: p.projectedEnd,
        status: p.status,
    }));
    data.steps.forEach(s => rows.push({
        key: s.key,
        moduleKey: s.moduleKey,
        phaseKey: s.phaseKey,
        type: 'subtask',
        name: s.name,
        moduleAbbrev: s.moduleAbbrev,
        actualStart: s.actualStart,
        actualEnd: s.actualEnd,
        projectedStart: s.projectedStart,
        projectedEnd: s.projectedEnd,
        status: s.status,
    }));
    return rows;
};

const ORPHAN_EVENTS_GROUP = 'pt-orphan-events';

const isModuleVisible = (moduleKey: string, visible: Set<string>) =>
    // 'unknown' = event whose module couldn't be resolved to the catalog; always allow into orphan row.
    moduleKey === 'unknown' || visible.has(moduleKey);

interface EventRow {
    key: string;
    name: string;
    moduleKey: string;
    parentKey: string; // module-header id for phases, parent-phase key for steps
}

// Unscheduled phases/steps (no dates → no bar) that nonetheless carry activity. We surface them
// as their own labeled rows so the names show in the title column, with their event clusters on them.
// When showUnscheduled is false the caller wants these date-less rows hidden, so we return none (their
// events then fall to the orphan group, which the caller likewise suppresses).
const collectEventRows = (data: ProgressData, depth: DepthMode, visible: Set<string>, showUnscheduled: boolean) => {
    const eventPhaseKeys = new Set(data.events.map(e => e.phaseKey).filter(Boolean) as string[]);
    const eventStepKeys = new Set(data.events.map(e => e.stepKey).filter((k): k is string => !!k));
    const scheduledPhaseKeys = new Set(data.phases.map(p => p.key));
    const scheduledStepKeys = new Set(data.steps.map(s => s.key));

    const unschedPhaseRows: EventRow[] = (showUnscheduled && (depth === 'phases' || depth === 'steps'))
        ? data.unscheduledPhases
            .filter(up => visible.has(up.moduleKey))
            .map(up => ({
                key: `${up.moduleKey}-${up.phaseId}`,
                name: up.name,
                moduleKey: up.moduleKey,
                parentKey: `mod-${up.moduleKey}`,
            }))
            .filter(r => eventPhaseKeys.has(r.key) && !scheduledPhaseKeys.has(r.key))
        : [];

    const unschedStepRows: EventRow[] = (showUnscheduled && depth === 'steps')
        ? data.unscheduledSteps
            .filter(us => visible.has(us.moduleKey))
            .map(us => ({
                key: `${us.moduleKey}-${us.phaseId}-${us.stepId}`,
                name: us.name,
                moduleKey: us.moduleKey,
                parentKey: `${us.moduleKey}-${us.phaseId}`,
            }))
            .filter(r => eventStepKeys.has(r.key) && !scheduledStepKeys.has(r.key))
        : [];

    // At module depth, a module with no scheduled phases has no aggregate bar — its events would
    // otherwise all collapse into the single orphan row. Give such modules (that actually carry
    // events) their own labeled, bar-less row, mirroring the unscheduled phase/step rows above.
    const aggModuleKeys = new Set(data.moduleAggregates.map(m => m.moduleKey));
    const eventModuleKeys = new Set(data.events.map(e => e.moduleKey));
    const unschedModuleRows: EventRow[] = (showUnscheduled && depth === 'modules')
        ? data.modules
            .filter(m => visible.has(m.key) && eventModuleKeys.has(m.key) && !aggModuleKeys.has(m.key))
            .map(m => ({
                key: `mod-${m.key}`,
                name: `${m.name} (${m.abbrev})`,
                moduleKey: m.key,
                parentKey: '',
            }))
        : [];

    const phaseRowKeys = new Set<string>([...scheduledPhaseKeys, ...unschedPhaseRows.map(r => r.key)]);
    const stepRowKeys = new Set<string>([...scheduledStepKeys, ...unschedStepRows.map(r => r.key)]);
    // Module rows that exist: the aggregate keys plus the unscheduled module rows just built.
    const moduleRowKeys = new Set<string>([
        ...data.moduleAggregates.map(a => a.key),
        ...unschedModuleRows.map(r => r.key),
    ]);

    return { unschedPhaseRows, unschedStepRows, unschedModuleRows, phaseRowKeys, stepRowKeys, moduleRowKeys };
};

// An undated task has no bar, but it still needs a header row to nest its dated subtasks —
// otherwise those subtasks would float to the top with no parent. A parent row exists for every
// undated phase that has any child to show: a dated subtask always, or (with Undated on) its own
// date-less subtask rows / event ticks.
const collectUnschedPhaseParents = (
    data: ProgressData,
    visible: Set<string>,
    unschedPhaseRows: EventRow[],
    unschedStepRows: EventRow[],
) => data.unscheduledPhases
    .filter(up => visible.has(up.moduleKey))
    .map(up => {
        const key = `${up.moduleKey}-${up.phaseId}`;
        const childKeys = [
            ...data.steps.filter(s => s.phaseKey === key).map(s => s.key),
            ...unschedStepRows.filter(r => r.parentKey === key).map(r => r.key),
        ];
        return { key, name: up.name, moduleKey: up.moduleKey, childKeys };
    })
    .filter(r => r.childKeys.length > 0 || unschedPhaseRows.some(er => er.key === r.key));

const eventRowKey = (
    data: ProgressData,
    depth: DepthMode,
    event: TimelineEvent,
    visible: Set<string>,
    phaseRowKeys: Set<string>,
    stepRowKeys: Set<string>,
    moduleRowKeys: Set<string>,
): string | null => {
    if (!isModuleVisible(event.moduleKey, visible)) return null;
    if (depth === 'modules') {
        const agg = data.moduleAggregates.find(m => m.moduleKey === event.moduleKey);
        if (agg) return agg.key;
        // No aggregate (module has no scheduled phases): use its unscheduled module row if we built one.
        const modKey = `mod-${event.moduleKey}`;
        return moduleRowKeys.has(modKey) ? modKey : ORPHAN_EVENTS_GROUP;
    }
    if (depth === 'phases') {
        return event.phaseKey && phaseRowKeys.has(event.phaseKey) ? event.phaseKey : ORPHAN_EVENTS_GROUP;
    }
    // steps depth: prefer step row; fall back to phase row; else orphan
    if (event.stepKey && stepRowKeys.has(event.stepKey)) return event.stepKey;
    if (event.phaseKey && phaseRowKeys.has(event.phaseKey)) return event.phaseKey;
    return ORPHAN_EVENTS_GROUP;
};

const hasOrphanEvents = (data: ProgressData, depth: DepthMode, visible: Set<string>, showUnscheduled: boolean): boolean => {
    // The orphan group is itself a date-less row, so hide it too when unscheduled rows are off.
    if (!showUnscheduled) return false;
    const { phaseRowKeys, stepRowKeys, moduleRowKeys } = collectEventRows(data, depth, visible, showUnscheduled);
    return data.events.some(e => eventRowKey(data, depth, e, visible, phaseRowKeys, stepRowKeys, moduleRowKeys) === ORPHAN_EVENTS_GROUP);
};

// The undated tasks/subtasks the chart leaves off entirely, so the banner can name exactly those
// and its counts match what the reader sees. An undated item that earns a row — an event-only row,
// or an undated-task header nesting dated subtasks — is on screen and so is not reported. Items
// below the current depth are left out too: they are absent because of the depth, not their dates.
export const collectUndatedNotShown = (
    data: ProgressData,
    depth: DepthMode,
    grouping: GroupingMode,
    visible: Set<string>,
    showUnscheduled: boolean,
): { phases: UnscheduledPhase[]; steps: UnscheduledStep[] } => {
    if (depth === 'modules') return { phases: [], steps: [] };

    const { unschedPhaseRows, unschedStepRows } = collectEventRows(data, depth, visible, showUnscheduled);
    const shownPhaseKeys = new Set(unschedPhaseRows.map(r => r.key));
    if (depth === 'steps' && grouping === 'nested') {
        collectUnschedPhaseParents(data, visible, unschedPhaseRows, unschedStepRows)
            .forEach(r => shownPhaseKeys.add(r.key));
    }
    const shownStepKeys = new Set(unschedStepRows.map(r => r.key));

    return {
        phases: data.unscheduledPhases.filter(p =>
            visible.has(p.moduleKey) && !shownPhaseKeys.has(`${p.moduleKey}-${p.phaseId}`)),
        steps: depth === 'steps'
            ? data.unscheduledSteps.filter(st =>
                visible.has(st.moduleKey) && !shownStepKeys.has(`${st.moduleKey}-${st.phaseId}-${st.stepId}`))
            : [],
    };
};

const buildSizerItems = (groupIds: string[], start: Date, end: Date): DataItem[] =>
    groupIds.map(gid => ({
        id: `sizer-${gid}`,
        group: gid,
        start,
        end,
        type: 'range',
        className: 'pt-row-sizer',
    } as DataItem));

const computeExpandableGroups = (data: ProgressData, depth: DepthMode, grouping: GroupingMode, visible: Set<string>, showUnscheduled: boolean): Set<string> => {
    const set = new Set<string>();
    if (grouping !== 'nested') return set;
    const { unschedPhaseRows, unschedStepRows } = collectEventRows(data, depth, visible, showUnscheduled);
    if (depth === 'phases' || depth === 'steps') {
        data.modules.forEach(m => {
            const hasChildren = data.phases.some(p => p.moduleKey === m.key)
                || unschedPhaseRows.some(r => r.moduleKey === m.key);
            if (hasChildren) set.add(`mod-${m.key}`);
        });
    }
    if (depth === 'steps') {
        data.phases.forEach(p => {
            const hasChildren = data.steps.some(s => s.phaseKey === p.key)
                || unschedStepRows.some(r => r.parentKey === p.key);
            if (hasChildren) set.add(p.key);
        });
    }
    return set;
};

const buildItems = (
    data: ProgressData,
    depth: DepthMode,
    grouping: GroupingMode,
    showEvents: boolean,
    showProjected: boolean,
    visible: Set<string>,
    showUnscheduled: boolean,
): DataItem[] => {
    const items: DataItem[] = [];
    const expandable = computeExpandableGroups(data, depth, grouping, visible, showUnscheduled);
    const rows = collectRows(data, depth);
    rows.forEach(r => {
        if (showProjected) {
            items.push({
                id: `bg-${r.key}`,
                group: r.key,
                start: r.projectedStart,
                end: r.projectedEnd,
                type: 'background',
                style: `background-color: ${PROJECTED_FILL};`,
            } as DataItem);
        }
        const color = statusColor(r.status as any);
        const label = rowDisplayLabel(r);
        const clickable = expandable.has(r.key) ? ' pt-clickable' : '';
        items.push({
            id: `bar-${r.key}`,
            group: r.key,
            content: label,
            start: r.actualStart,
            end: r.actualEnd,
            type: 'range',
            className: `pt-bar${clickable}`,
            title: `<strong>${label}</strong><br/>${r.status}<br/>${moment(r.actualStart).format('MM-DD-YYYY')} to ${moment(r.actualEnd).format('MM-DD-YYYY')}`,
            style: `background-color: ${color}; border-color: ${color}; color: #000;`,
        } as DataItem);
    });
    if (showEvents) {
        // Phase rows that have any resubmission file — decided across all the grant's events so every
        // file in such a phase is tagged, not just the ones in a given tooltip.
        const resubPhaseKeys = new Set(
            data.events.filter(e => e.type === 'File' && e.resubmission != null).map(e => e.phaseKey),
        );
        const { phaseRowKeys, stepRowKeys, moduleRowKeys } = collectEventRows(data, depth, visible, showUnscheduled);
        const clusters = clusterEventsByDay(data.events, e => eventRowKey(data, depth, e, visible, phaseRowKeys, stepRowKeys, moduleRowKeys));
        clusters.forEach(c => {
            // With unscheduled rows hidden, their events route to the orphan group — which is also a
            // date-less row, so drop those clusters rather than render an orphan row.
            if (!showUnscheduled && c.rowKey === ORPHAN_EVENTS_GROUP) return;
            const clickable = expandable.has(c.rowKey) ? ' pt-clickable' : '';
            items.push({
                id: `cluster-${c.key}`,
                group: c.rowKey,
                start: c.date,
                // 'box' (center-aligned), not 'point': a point item is culled the instant its date
                // crosses the right window edge (vis PointItem.isVisible has no right-side width
                // tolerance), so edge ticks vanish when zooming in. A center-aligned box stays
                // visible while any part of the marker is within the window — symmetric on both edges.
                type: 'box',
                className: `pt-event-tick${clickable}`,
                content: clusterContentHtml(c),
                // No native `title`: both single ticks and (after patchClusterCounts clears their title)
                // collapsed clusters use the interactive React tooltip, driven by the structured pt*
                // fields below. tipDate lets a cluster sort its merged day-ticks chronologically.
                eventCount: c.events.length,
                fileCount: c.fileCount,
                commentCount: c.commentCount,
                noteCount: c.noteCount,
                tipDate: c.date.getTime(),
                ptHeader: tipContextHeader(c.events[0], depth),
                ptDateStr: moment(c.date).format('MM-DD-YYYY'),
                ptEvents: c.events.map(e => ({
                    type: e.type,
                    label: e.label,
                    commentType: e.commentType,
                    fileId: e.fileId,
                    fileName: e.fileName,
                    phaseName: e.phaseName,
                    stepName: e.stepName,
                    userName: e.userName,
                    resubmission: e.resubmission,
                    phaseHasResubmissions: resubPhaseKeys.has(e.phaseKey),
                    // Location for the tooltip's "open" deep links.
                    grantId: e.grantId,
                    moduleTypeId: e.moduleTypeId,
                    phaseId: e.phaseId,
                    stepId: e.stepId,
                })) as TooltipEvent[],
            } as DataItem & {
                eventCount: number; fileCount: number; commentCount: number; noteCount: number;
                tipDate: number; ptHeader: string; ptDateStr: string; ptEvents: TooltipEvent[];
            });
        });
    }
    return items;
};

export const buildGroups = (
    data: ProgressData,
    depth: DepthMode,
    mode: GroupingMode,
    visible: Set<string>,
    includeOrphanEvents: boolean,
    showUnscheduled: boolean,
): DataGroup[] => {
    const groups: DataGroup[] = [];
    const colorFor = (mk: string) => data.modules.find(m => m.key === mk)?.color ?? '#000';

    const moduleHeaderHtml = (m: ProgressData['modules'][number]) =>
        `<span style="color:${m.color};font-weight:600">${m.name} (${m.abbrev})</span>`;

    const { unschedPhaseRows, unschedStepRows, unschedModuleRows } = collectEventRows(data, depth, visible, showUnscheduled);
    // Unscheduled rows have no bar; mark them visually as events-only.
    const eventOnlyLabel = (name: string, padding: number) =>
        `<span style="padding-left:${padding}px; font-style:italic; color:#777;">${name}</span>`;

    if (includeOrphanEvents) {
        groups.push({
            id: ORPHAN_EVENTS_GROUP,
            content: `<span style="font-style:italic; color:#777;">Events (no schedule)</span>`,
            visible: true,
        } as DataGroup);
    }

    if (depth === 'modules') {
        data.moduleAggregates.forEach(m => {
            groups.push({
                id: m.key,
                content: `<span style="color:${m.color};font-weight:600">${m.name} (${m.abbrev})</span>`,
                visible: visible.has(m.moduleKey),
            } as DataGroup);
        });
        // Modules with only undated events (no aggregate bar) get an events-only row of their own.
        unschedModuleRows.forEach(r => {
            groups.push({
                id: r.key,
                content: eventOnlyLabel(r.name, 0),
                visible: visible.has(r.moduleKey),
            } as DataGroup);
        });
        return groups;
    }

    if (depth === 'phases') {
        if (mode === 'nested') {
            data.modules.forEach(m => {
                const phaseKeys = data.phases.filter(p => p.moduleKey === m.key).map(p => p.key);
                const unschedKeys = unschedPhaseRows.filter(r => r.moduleKey === m.key).map(r => r.key);
                const allKeys = [...phaseKeys, ...unschedKeys];
                if (!allKeys.length) return;
                groups.push({
                    id: `mod-${m.key}`,
                    content: moduleHeaderHtml(m),
                    nestedGroups: allKeys,
                    visible: visible.has(m.key),
                } as DataGroup);
            });
            data.phases.forEach(p => {
                groups.push({
                    id: p.key,
                    content: `<span style="padding-left:14px;">${p.name}</span>`,
                    visible: visible.has(p.moduleKey),
                } as DataGroup);
            });
            unschedPhaseRows.forEach(r => {
                groups.push({
                    id: r.key,
                    content: eventOnlyLabel(r.name, 14),
                    visible: visible.has(r.moduleKey),
                } as DataGroup);
            });
        } else {
            data.phases.forEach(p => {
                const colorStyle = mode === 'flat-prefix' ? `color:${colorFor(p.moduleKey)};` : '';
                groups.push({
                    id: p.key,
                    content: `<span style="${colorStyle}">${p.name}</span>`,
                    visible: visible.has(p.moduleKey),
                } as DataGroup);
            });
            unschedPhaseRows.forEach(r => {
                groups.push({
                    id: r.key,
                    content: eventOnlyLabel(r.name, 0),
                    visible: visible.has(r.moduleKey),
                } as DataGroup);
            });
        }
        return groups;
    }

    // depth === 'steps'
    if (mode === 'nested') {
        const unschedPhaseParents = collectUnschedPhaseParents(data, visible, unschedPhaseRows, unschedStepRows);

        data.modules.forEach(m => {
            const phaseKeysForModule = data.phases.filter(p => p.moduleKey === m.key).map(p => p.key);
            const unschedPhaseKeys = unschedPhaseParents.filter(r => r.moduleKey === m.key).map(r => r.key);
            const allKeys = [...phaseKeysForModule, ...unschedPhaseKeys];
            if (!allKeys.length) return;
            groups.push({
                id: `mod-${m.key}`,
                content: moduleHeaderHtml(m),
                nestedGroups: allKeys,
                visible: visible.has(m.key),
            } as DataGroup);
        });
        data.phases.forEach(p => {
            const stepKeys = data.steps.filter(s => s.phaseKey === p.key).map(s => s.key);
            const unschedKeys = unschedStepRows.filter(r => r.parentKey === p.key).map(r => r.key);
            const allKeys = [...stepKeys, ...unschedKeys];
            groups.push({
                id: p.key,
                content: `<span style="font-weight:500; padding-left:14px;">${p.name}</span>`,
                nestedGroups: allKeys.length ? allKeys : undefined,
                visible: visible.has(p.moduleKey),
            } as DataGroup);
        });
        data.steps.forEach(s => {
            groups.push({
                id: s.key,
                content: `<span style="padding-left:28px;">${s.name}</span>`,
                visible: visible.has(s.moduleKey),
            } as DataGroup);
        });
        // Undated task headers: no bar of their own, but they nest their dated subtasks (and any
        // date-less subtask rows). Italic so they read as a header, not a scheduled task.
        unschedPhaseParents.forEach(r => {
            groups.push({
                id: r.key,
                content: eventOnlyLabel(r.name, 14),
                nestedGroups: r.childKeys.length ? r.childKeys : undefined,
                visible: visible.has(r.moduleKey),
            } as DataGroup);
        });
        // Unscheduled steps (events only) nest under their parent phase.
        unschedStepRows.forEach(r => {
            groups.push({
                id: r.key,
                content: eventOnlyLabel(r.name, 28),
                visible: visible.has(r.moduleKey),
            } as DataGroup);
        });
    } else {
        data.steps.forEach(s => {
            const colorStyle = mode === 'flat-prefix' ? `color:${colorFor(s.moduleKey)};` : '';
            const content = `<span style="${colorStyle}">${s.name}</span>`;
            groups.push({
                id: s.key,
                content,
                visible: visible.has(s.moduleKey),
            } as DataGroup);
        });
        unschedStepRows.forEach(r => {
            groups.push({
                id: r.key,
                content: eventOnlyLabel(r.name, 0),
                visible: visible.has(r.moduleKey),
            } as DataGroup);
        });
        unschedPhaseRows.forEach(r => {
            groups.push({
                id: r.key,
                content: eventOnlyLabel(r.name, 0),
                visible: visible.has(r.moduleKey),
            } as DataGroup);
        });
    }
    return groups;
};

export const useVisTimelineController = (props: VisTimelineControllerProps): VisTimelineViewProps => {
    const app = useAppContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const timelineRef = useRef<Timeline | null>(null);
    const itemsRef = useRef<DataSet<DataItem> | null>(null);
    const groupsRef = useRef<DataSet<DataGroup> | null>(null);
    // Interactive hover tooltip: vis's native title can't hold clickable links, so we render our own.
    const [hoverTip, setHoverTip] = useState<HoverTip | null>(null);
    const tipCloseTimer = useRef<number | null>(null);
    // Id of the event tick the tooltip is currently anchored to. Re-hovering the same tick is a no-op
    // (so a dwelling pointer doesn't make the panel jitter); hovering a genuinely different tick
    // re-anchors the panel to it.
    const tipItemRef = useRef<string | number | null>(null);

    const cancelTipClose = () => {
        if (tipCloseTimer.current !== null) {
            window.clearTimeout(tipCloseTimer.current);
            tipCloseTimer.current = null;
        }
    };
    const closeTip = () => {
        cancelTipClose();
        tipItemRef.current = null;
        setHoverTip(null);
    };
    // Keep the tooltip alive after the pointer leaves the tick so the user has time to move into it
    // (and click a file). It closes immediately on the panel's own mouseleave, so this is just the
    // grace period for the tick → panel hop.
    const scheduleTipClose = () => {
        cancelTipClose();
        tipCloseTimer.current = window.setTimeout(closeTip, 600);
    };
    const onTipEnter = () => cancelTipClose();
    const onTipLeave = () => closeTip();

    const downloadEventFile = async (fileId: string, fileName: string) => {
        app.showLoadPanel();
        const error = await downloadFileById(fileId, fileName, app.configuration?.streamingFileTransfer);
        app.hideLoadPanel();
        if (error) app.errorToast(error);
    };

    // Open an event's task/subtask. The Progress Monitor sits on the grant page already, so this sets
    // the hash rather than navigating; GrantDetailPhases listens for the change and opens the module,
    // then the existing deep link path expands the task and selects the subtask.
    const openEventLocation = (ev: TooltipEvent, includeSubtask: boolean) => {
        const hash = buildDeepLinkHash(ev.moduleTypeId, ev.phaseId, includeSubtask ? ev.stepId : null);
        if (!hash) return;
        closeTip();
        window.location.hash = hash;
    };

    const [raw, setRaw] = useState<RawProgressData | null>(null);
    const [loading, setLoading] = useState(true);
    // Default to the original progress monitor's view: Task level (see module default on load below).
    const [depthMode, setDepthMode] = useState<DepthMode>('phases');
    const [groupingMode, setGroupingMode] = useState<GroupingMode>('nested');
    const [visibleModuleKeys, setVisibleModuleKeys] = useState<string[]>([]);
    const [showEvents, setShowEvents] = useState(true);
    const [showProjected, setShowProjected] = useState(false);
    const [showLabels, setShowLabels] = useState(true);
    const [showUnscheduled, setShowUnscheduled] = useState(false);
    // Current visible window; mirrors vis's window so the zoom slider and horizontal scrollbar can
    // both drive it and reflect Ctrl+scroll / drag-pan changes.
    const [windowStart, setWindowStart] = useState<Date>(new Date());
    const [windowEnd, setWindowEnd] = useState<Date>(new Date());

    // Re-seed the module selection only when the grant changes, not on a note-add refresh — a
    // refreshToken bump reloads the data but keeps whatever modules the user has toggled.
    const lastGrantIdRef = useRef<string | null>(null);
    // On an in-place refresh (note add) we capture the current zoom/pan window here so the rebuilt
    // timeline can restore it instead of snapping back to the full range.
    const pendingWindowRef = useRef<{ start: Date; end: Date } | null>(null);

    useEffect(() => {
        let cancelled = false;
        // Same grant + a new refreshToken = an in-place refresh (e.g. a note was added). Grab the live
        // window now so we can re-apply it once the rebuilt timeline mounts; an initial load or a grant
        // switch intentionally keeps the default full-range view.
        if (lastGrantIdRef.current === props.grantData.id) {
            const w = (timelineRef.current as any)?.getWindow?.();
            if (w?.start && w?.end) pendingWindowRef.current = { start: w.start, end: w.end };
        }
        setLoading(true);
        loadCrossModuleData(props.grantData).then(r => {
            if (cancelled) return;
            setRaw(r);
            if (lastGrantIdRef.current !== props.grantData.id) {
                lastGrantIdRef.current = props.grantData.id;
                // Match the original monitor: start with only the DC module selected (fall back to all
                // modules if this grant has no DC module).
                const dcOnly = r.modules.filter(m => m.key === 'dc').map(m => m.key);
                setVisibleModuleKeys(dcOnly.length ? dcOnly : r.modules.map(m => m.key));
            }
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [props.grantData, props.refreshToken]);

    const data = useMemo(() => (raw ? buildProgressData(raw) : null), [raw]);

    // What the banner reports. Derived from the same row rules the chart builds from, so a task or
    // subtask is named there only when no row was drawn for it.
    const undatedNotShown = useMemo(
        () => (data
            ? collectUndatedNotShown(data, depthMode, groupingMode, new Set(visibleModuleKeys), showUnscheduled)
            : { phases: [], steps: [] }),
        [data, depthMode, groupingMode, visibleModuleKeys, showUnscheduled]
    );

    // Absorbed tasks the banner names. At the module depth no task rows are drawn, so none is missing.
    const absorbedNotShown = useMemo(
        () => (data && depthMode !== 'modules'
            ? data.absorbedPhases.filter(a => isModuleVisible(a.moduleKey, new Set(visibleModuleKeys)))
            : []),
        [data, depthMode, visibleModuleKeys]
    );

    // Reset the window when the data set changes (initial load, depth change that rebuilds data).
    useEffect(() => {
        if (data) {
            setWindowStart(data.rangeMin);
            setWindowEnd(data.rangeMax);
        }
    }, [data]);

    const setTimelineWindow = (start: Date, end: Date) => {
        timelineRef.current?.setWindow(start, end, { animation: false });
    };

    // Slider value: 0 = fully zoomed out (full range), 100 = fully zoomed in (zoomMin = 7 days).
    // Logarithmic mapping mirrors how vis zoom actually feels (each step roughly halves the span).
    const zoomPercent = useMemo(() => {
        if (!data) return 0;
        const R = data.rangeMax.getTime() - data.rangeMin.getTime();
        const W = windowEnd.getTime() - windowStart.getTime();
        if (R <= MIN_WINDOW_MS || W >= R) return 0;
        if (W <= MIN_WINDOW_MS) return 100;
        return Math.max(0, Math.min(100, (Math.log(W / R) / Math.log(MIN_WINDOW_MS / R)) * 100));
    }, [windowStart, windowEnd, data]);

    const setZoomPercent = (v: number) => {
        if (!data) return;
        const R = data.rangeMax.getTime() - data.rangeMin.getTime();
        if (R <= MIN_WINDOW_MS) return;
        const W = R * Math.pow(MIN_WINDOW_MS / R, Math.max(0, Math.min(100, v)) / 100);
        const center = (windowStart.getTime() + windowEnd.getTime()) / 2;
        let start = center - W / 2;
        let end = center + W / 2;
        const minT = data.rangeMin.getTime();
        const maxT = data.rangeMax.getTime();
        if (start < minT) { start = minT; end = start + W; }
        if (end > maxT) { end = maxT; start = end - W; }
        setTimelineWindow(new Date(start), new Date(end));
    };

    // vis renders a cluster's number as the count of sub-items it merged (here, day-ticks). We want the
    // actual number of events downstream, so rewrite each cluster's content to the sum of its ticks'
    // eventCount. This only touches the rendered DOM (no vis redraw), so it's safe to call from 'changed'.
    const patchClusterCounts = (tl: any) => {
        if (!tl) return;
        const clusters: any[] = getItemSet(tl)?.clusters;
        if (!clusters?.length) return;
        clusters.forEach(cluster => {
            const uiItems: any[] = cluster?.data?.uiItems ?? [];
            let total = 0;
            let noteTotal = 0;
            let otherTotal = 0;
            uiItems.forEach(ui => {
                const d = ui?.data;
                total += d?.eventCount ?? 1;
                noteTotal += d?.noteCount ?? 0;
                otherTotal += (d?.fileCount ?? 0) + (d?.commentCount ?? 0);
            });
            const html = eventTriangleHtml(total, tickKind(noteTotal, otherTotal));
            if (cluster.data && cluster.data.content !== html) {
                cluster.data.content = html;
                cluster.dirty = true;
                const el = cluster.dom?.content;
                if (el && el.innerHTML !== html) el.innerHTML = html;
            }
            // Clusters drive the interactive React popover (see itemover) just like single ticks, so
            // clear any native title — otherwise vis would also show its non-interactive beige popup.
            if (cluster.data) cluster.data.title = undefined;
        });
    };

    useEffect(() => {
        if (!containerRef.current || !data) return;
        const visible = new Set(visibleModuleKeys);
        const orphan = hasOrphanEvents(data, depthMode, visible, showUnscheduled);
        const groupList = buildGroups(data, depthMode, groupingMode, visible, orphan, showUnscheduled);
        const items = new DataSet<DataItem>([
            ...buildItems(data, depthMode, groupingMode, showEvents, showProjected, visible, showUnscheduled),
            ...buildSizerItems(groupList.map(g => g.id as string), data.rangeMin, data.rangeMax),
        ]);
        const groups = new DataSet<DataGroup>(groupList);
        timelineRef.current = new Timeline(containerRef.current, items, groups, makeTimelineOptions(data, false));
        itemsRef.current = items;
        groupsRef.current = groups;
        // Self-healing redraw: vis can still throw "Cannot redraw item: no parent attached" when
        // clustering meets a collapsed/expanded nested group on some paths. If a redraw throws that,
        // regenerate the clusters and retry once so the error never reaches the UI. vis reads
        // _redraw/_origRedraw as instance properties at call time, so reassigning them here is honored
        // by vis's own internal redraw calls (we still go through a rAF throttle, like vis does).
        {
            const tl = timelineRef.current as any;
            const origRedraw = tl._origRedraw as () => void;
            if (typeof origRedraw === 'function') {
                const healingRedraw = () => {
                    try {
                        origRedraw();
                    } catch (err) {
                        if (String((err as Error)?.message).includes('no parent attached')) {
                            try { regenerateClusters(getItemSet(tl)); origRedraw(); } catch { /* give up quietly */ }
                        } else {
                            throw err;
                        }
                    }
                };
                tl._origRedraw = healingRedraw;
                tl._redraw = rafThrottle(healingRedraw);
            }
        }
        // Clicking an item whose row has nested children should toggle that row's collapsed state,
        // mirroring what vis does when you click the label's arrow. vis's
        // toggleGroupShowNested expects an internal Group instance from itemSet.groups, not a DataSet item.
        timelineRef.current.on('click', (e: any) => {
            if (!e?.item || !groupsRef.current || !itemsRef.current || !timelineRef.current) return;
            const item = itemsRef.current.get(e.item) as DataItem | null;
            if (!item) return;
            const groupId = (item as any).group;
            if (groupId == null) return;
            const t = timelineRef.current as any;
            const itemSet = getItemSet(t);
            const groupInstance = itemSet?.groups?.[groupId];
            if (groupInstance?.nestedGroups?.length && typeof itemSet.toggleGroupShowNested === 'function') {
                itemSet.toggleGroupShowNested(groupInstance);
                // Collapsing a parent detaches its children's cluster items; on re-expand vis's throttled
                // redraw would otherwise try to draw those stale clusters and throw. Regenerate the
                // clusters now (before that rAF redraw) so fresh, parented cluster items replace them.
                regenerateClusters(itemSet);
            }
        });
        // After every redraw, rewrite cluster bubbles to show the total events they represent.
        timelineRef.current.on('changed', () => patchClusterCounts(timelineRef.current));
        // Interactive tooltip: show our own panel when hovering a single event tick (it carries the
        // structured ptEvents). Bars/sizers/collapsed clusters have no ptEvents → ignored (clusters keep
        // their native title). The panel is hover-into so file links inside it stay clickable.
        timelineRef.current.on('itemover', (e: any) => {
            if (!itemsRef.current || !e?.item) return;
            // Resolve the day(s) of structured events behind whatever was hovered. A single tick is a
            // real DataSet item carrying ptEvents directly; a collapsed cluster isn't in the DataSet, so
            // pull it from the ItemSet and aggregate its merged day-ticks' pt* fields (one section/day).
            let header: string | undefined;
            let days: HoverTipDay[] = [];
            let extraDays = 0;
            const single = itemsRef.current.get(e.item) as any;
            if (single?.ptEvents) {
                header = single.ptHeader;
                days = [{ dateStr: single.ptDateStr, events: single.ptEvents as TooltipEvent[] }];
            } else {
                const cluster = getItemSet(timelineRef.current)?.getItemById?.(e.item);
                const ticks: any[] = (cluster?.data?.uiItems ?? [])
                    .map((ui: any) => ui?.data)
                    .filter((d: any) => d?.ptEvents);
                if (ticks.length) {
                    header = ticks[0].ptHeader;
                    // Merge same-date ticks into one day each (newest first); the tooltip body scrolls.
                    days = mergeTicksToDays(ticks);
                }
            }
            if (header === undefined || !days.length) return;
            cancelTipClose();
            // Same marker already shown → don't re-anchor (avoids jitter while the pointer dwells on it).
            if (tipItemRef.current === e.item) return;
            // Anchor to the marker's on-screen box (not the cursor) so the panel sits in a stable spot
            // just under it; the user moves straight down into it. getBoundingClientRect gives viewport
            // coords, which is what the portal-rendered fixed panel positions against.
            const target = e.event?.target as HTMLElement | undefined;
            const markerEl = target?.closest('.vis-item') as HTMLElement | null;
            const rect = markerEl?.getBoundingClientRect();
            const cx = e.event?.clientX ?? 0;
            const cy = e.event?.clientY ?? 0;
            tipItemRef.current = e.item;
            setHoverTip({
                anchorLeft: rect ? rect.left : cx,
                anchorRight: rect ? rect.right : cx,
                anchorTop: rect ? rect.top : cy,
                anchorBottom: rect ? rect.bottom : cy,
                header,
                depth: depthMode,
                days,
                extraDays,
            });
        });
        timelineRef.current.on('itemout', (e: any) => {
            const item = e?.item && itemsRef.current ? (itemsRef.current.get(e.item) as any) : null;
            if (item && !item.ptEvents) return; // leaving a non-event item shouldn't close an open tip
            scheduleTipClose();
        });
        // Mirror vis's window into state so the zoom slider and horizontal scrollbar stay in sync with
        // Ctrl+scroll zooms and drag-pans.
        timelineRef.current.on('rangechanged', (e: any) => {
            if (e?.start && e?.end) {
                setWindowStart(e.start instanceof Date ? e.start : new Date(e.start));
                setWindowEnd(e.end instanceof Date ? e.end : new Date(e.end));
            }
        });
        // Clustering computes its scale from the measured center-panel width. On first paint that width
        // can still be 0 (the tab/container isn't laid out yet), so the cluster pass produces nothing
        // until a later rangechange re-measures it — which is why bubbles only appeared after a manual
        // zoom. Poll until the container has a real width, then redraw() to push that width into vis's
        // domProps and nudge the window 1ms (the same rangechange a zoom fires) so clusters compute.
        let clusterTries = 0;
        const primeClusters = () => {
            const tl = timelineRef.current;
            if (!tl || !containerRef.current) return;
            if (containerRef.current.clientWidth === 0) {
                if (++clusterTries < 120) requestAnimationFrame(primeClusters);
                return;
            }
            // Push the now-known width into vis's domProps, then a 1ms window nudge fires the
            // rangechange that runs the cluster pass against the correct width.
            tl.redraw();
            const { start, end } = tl.getWindow();
            tl.setWindow(start, new Date(end.getTime() - 1), { animation: false });
            tl.setWindow(start, end, { animation: false });
            // The clusters are now computed and attached, but they live in a separate collection from
            // regular items and only get laid out into their rows when the groups re-stack (which a
            // manual vertical scroll forced). Mark the groups dirty and redraw so they paint on load.
            const anyTl = tl as any;
            const itemSet = anyTl.itemSet || anyTl._itemSet || anyTl.body?.itemSet;
            if (itemSet?.markDirty) {
                itemSet.markDirty({ refreshItems: true, restackGroups: true });
                tl.redraw();
            }
            patchClusterCounts(tl);
        };
        requestAnimationFrame(primeClusters);
        // If this rebuild came from an in-place refresh (e.g. a note was just added), restore the
        // pre-refresh zoom/pan window so the new tick shows up without the view snapping to full range.
        // primeClusters reads getWindow(), so setting it now (synchronously) keeps its nudges on this window.
        if (pendingWindowRef.current) {
            const { start, end } = pendingWindowRef.current;
            pendingWindowRef.current = null;
            timelineRef.current.setWindow(start, end, { animation: false });
        }
        return () => {
            closeTip();
            timelineRef.current?.destroy();
            timelineRef.current = null;
            itemsRef.current = null;
            groupsRef.current = null;
        };
    }, [data, depthMode, groupingMode]);

    useEffect(() => {
        if (!data || !itemsRef.current || !groupsRef.current) return;
        const visible = new Set(visibleModuleKeys);
        const groupIds = groupsRef.current.getIds() as string[];
        itemsRef.current.clear();
        itemsRef.current.add([
            ...buildItems(data, depthMode, groupingMode, showEvents, showProjected, visible, showUnscheduled),
            ...buildSizerItems(groupIds, data.rangeMin, data.rangeMax),
        ]);
    }, [data, depthMode, groupingMode, showEvents, showProjected, visibleModuleKeys, showUnscheduled]);

    useEffect(() => {
        if (!timelineRef.current) return;
        // Keep maxHeight constant so the container is the same size in both modes.
        // verticalScroll has to stay on so vis still clips content past maxHeight; the
        // scrollbar lives in the labels panel, which is just collapsed to width 0 when
        // labels are hidden — clicking inside the timeline body still wheel-scrolls.
        timelineRef.current.setOptions({ maxHeight: 400, verticalScroll: true });
        timelineRef.current.redraw();
    }, [showLabels]);

    // Stamp today's date on the current-time line so CSS can render it as a label. vis adds the
    // current-time bar a frame or two after (re)creating the timeline, so a single immediate query can
    // miss it (label then stays blank until the hourly re-stamp). Retry on animation frames until the
    // bar exists, and re-run whenever the timeline is rebuilt (depth/grouping change makes a new bar).
    useEffect(() => {
        if (!containerRef.current) return;
        let raf = 0;
        let tries = 0;
        const stamp = () => {
            const el = containerRef.current?.querySelector('.vis-current-time') as HTMLElement | null;
            if (el) el.setAttribute('data-today', moment().format('MMM D'));
            return !!el;
        };
        const stampWhenReady = () => {
            if (stamp()) return;
            if (++tries < 120) raf = requestAnimationFrame(stampWhenReady);
        };
        stampWhenReady();
        const id = window.setInterval(stamp, 60 * 60 * 1000); // re-stamp hourly to survive day rollovers
        return () => { cancelAnimationFrame(raf); window.clearInterval(id); };
    }, [data, depthMode, groupingMode]);

    useEffect(() => {
        if (!groupsRef.current || !itemsRef.current || !data) return;
        const visible = new Set(visibleModuleKeys);
        const orphan = hasOrphanEvents(data, depthMode, visible, showUnscheduled);
        const groupList = buildGroups(data, depthMode, groupingMode, visible, orphan, showUnscheduled);
        groupsRef.current.clear();
        groupsRef.current.add(groupList);
        // Sizers are tied to group ids; refresh them when groups change.
        const existing = itemsRef.current.getIds().filter((id: string | number) => String(id).startsWith('sizer-'));
        itemsRef.current.remove(existing);
        itemsRef.current.add(buildSizerItems(groupList.map(g => g.id as string), data.rangeMin, data.rangeMax));
    }, [visibleModuleKeys, data, depthMode, groupingMode, showUnscheduled]);

    // Export the full vertical content without disturbing the on-screen view. We clone the captured
    // region (toolbar + banner + legend stay as-is), then build a throwaway, unbounded timeline into
    // the cloned container so every row lays out. The live timeline is never touched → no flicker.
    const exportPng = async (captureEl: HTMLElement | null) => {
        if (!captureEl) return;
        if (!data || !containerRef.current) {
            await exportTimelinePng(captureEl, 'progress-timeline');
            return;
        }
        const liveWidth = containerRef.current.offsetWidth;
        const clone = captureEl.cloneNode(true) as HTMLElement;
        const cloneContainer = clone.querySelector('.pt-vis-container') as HTMLElement | null;
        if (!cloneContainer) {
            await exportTimelinePng(captureEl, 'progress-timeline');
            return;
        }
        // The export is just the timeline contents (and legend) — drop the toolbar of filters/buttons,
        // the "not shown" banner, and the horizontal scrollbar.
        clone.querySelector('.pt-vis-toolbar')?.remove();
        clone.querySelector('.pt-unscheduled-banner')?.remove();
        clone.querySelector('.pt-h-scroll')?.remove();
        cloneContainer.innerHTML = '';
        cloneContainer.style.width = `${liveWidth}px`;
        // Place the clone below the document (positive coords, off the bottom of the page) rather than
        // using position:fixed at a negative offset — html2canvas mis-renders vis's absolutely-positioned
        // content under a fixed/negative ancestor, which is why the body came out blank. Below-document +
        // z-index -1 keeps it out of the viewport so there's no flicker.
        clone.style.position = 'absolute';
        clone.style.left = '0';
        clone.style.top = `${document.documentElement.scrollHeight + 50}px`;
        clone.style.width = `${captureEl.offsetWidth}px`;
        clone.style.background = '#ffffff';
        clone.style.zIndex = '-1';
        clone.style.pointerEvents = 'none';
        document.body.appendChild(clone);

        const visible = new Set(visibleModuleKeys);
        const orphan = hasOrphanEvents(data, depthMode, visible, showUnscheduled);
        const groupList = buildGroups(data, depthMode, groupingMode, visible, orphan, showUnscheduled);
        const items = new DataSet<DataItem>([
            ...buildItems(data, depthMode, groupingMode, showEvents, showProjected, visible, showUnscheduled),
            ...buildSizerItems(groupList.map(g => g.id as string), data.rangeMin, data.rangeMax),
        ]);
        const groups = new DataSet<DataGroup>(groupList);
        const tmp = new Timeline(cloneContainer, items, groups, makeTimelineOptions(data, true));

        // vis renders asynchronously and fires 'changed' repeatedly during layout. Wait until it goes
        // quiet (or a hard cap) so we don't snapshot a half-drawn timeline.
        await new Promise<void>(res => {
            let t = window.setTimeout(res, 2000);
            const bump = () => { window.clearTimeout(t); t = window.setTimeout(res, 200); };
            tmp.on('changed', bump);
            bump();
        });
        // Force one more redraw so the clone's panel width is measured and cluster bubbles render. The
        // export intentionally shows the entire timeline (full date range), not the on-screen zoom.
        tmp.redraw();
        await new Promise<void>(res => requestAnimationFrame(() => requestAnimationFrame(() => res())));
        // Rewrite cluster bubbles to show total events (same as the live timeline).
        patchClusterCounts(tmp);
        // Stamp today's date on the cloned current-time line so its label renders (same as the live one).
        const ct = cloneContainer.querySelector('.vis-current-time') as HTMLElement | null;
        if (ct) ct.setAttribute('data-today', moment().format('MMM D'));

        try {
            await exportTimelinePng(clone, 'progress-timeline');
        } finally {
            tmp.destroy();
            if (clone.parentNode) clone.parentNode.removeChild(clone);
        }
    };

    return {
        containerRef,
        exportPng,
        loading,
        hasData: !!data,
        data,
        modules: data?.modules ?? [],
        visibleModuleKeys,
        setVisibleModuleKeys,
        depthMode,
        setDepthMode,
        groupingMode,
        setGroupingMode,
        showEvents,
        setShowEvents,
        showProjected,
        setShowProjected,
        showLabels,
        setShowLabels,
        showUnscheduled,
        setShowUnscheduled,
        undatedNotShown,
        absorbedNotShown,
        windowStart,
        windowEnd,
        dataRangeMin: data?.rangeMin ?? new Date(),
        dataRangeMax: data?.rangeMax ?? new Date(),
        setTimelineWindow,
        zoomPercent,
        setZoomPercent,
        hoverTip,
        onTipEnter,
        onTipLeave,
        downloadEventFile,
        openEventLocation,
    };
};
